import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Sparkles } from "lucide-react";
import ParticleField from "./ParticleField";
import { scrollToId } from "./shared";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const go = (id) => scrollToId(id);

  return (
    <section
      ref={ref}
      id="hero"
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden"
    >
      <motion.div style={{ scale }} className="absolute inset-0">
        <ParticleField />
      </motion.div>
      <div className="glow-blob left-[-10%] top-[10%] h-[420px] w-[420px] bg-[#00f0ff]" />
      <div className="glow-blob right-[-8%] bottom-[5%] h-[480px] w-[480px] bg-[#b026ff]" />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#05050a]" />

      <motion.div
        style={{ y, opacity }}
        className="relative z-10 mx-auto w-full max-w-7xl px-6"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 backdrop-blur-md"
        >
          <Sparkles className="h-4 w-4 text-[#00f0ff]" />
          <span className="font-mono-tech text-xs uppercase tracking-[0.3em] text-[#a0aec0]">
            IT Consulting & Creative Solutions
          </span>
        </motion.div>

        <h1 className="font-display max-w-4xl text-5xl font-bold leading-[1.02] md:text-7xl lg:text-8xl">
          <span className="block text-white">Powering your</span>
          <span className="text-gradient block">digital future</span>
        </h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-8 max-w-xl text-base leading-relaxed text-[#a0aec0] md:text-lg"
        >
          Gabicom delivers end-to-end technology — from hardware sales and cloud
          infrastructure to cybersecurity, creative design and 24/7 IT support.
        </motion.p>

        <div className="mt-10 flex flex-wrap items-center gap-4">
          <button
            onClick={() => go("contact")}
            data-testid="hero-cta-primary"
            className="group flex items-center gap-2 rounded-full bg-gradient-to-r from-[#00f0ff] to-[#b026ff] px-7 py-4 font-semibold text-[#05050a] transition-transform hover:scale-105"
          >
            Start a project
            <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          <button
            onClick={() => go("services")}
            data-testid="hero-cta-secondary"
            className="rounded-full border border-white/15 px-7 py-4 font-semibold text-white transition-colors hover:border-[#00f0ff]/60 hover:text-[#00f0ff]"
          >
            Explore services
          </button>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#a0aec0]"
      >
        Scroll to explore
      </motion.div>
    </section>
  );
}
